/**
 * Cache-first GT live state for the launched workspace.
 *
 *   - Load reads the server's last published snapshot; it never calls GT (AC-13).
 *   - With no usable snapshot, ONE read-only initial fetch starts on its own;
 *     after that GT is read again only when someone presses Refresh (Q3). The
 *     server refuses an "initial" fetch once a snapshot exists, so a second tab
 *     cannot turn this into an automatic refresh either.
 *   - While a refresh runs the hook polls, bounded: it stops on success,
 *     failure, or POLL_BUDGET_MS, whichever comes first (AC-17).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, getApiUrl } from '../../utils/apiConfig';

export const POLL_INTERVAL_MS = 3000;
export const POLL_BUDGET_MS = 16 * 60 * 1000;

const messageOf = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

export default function useGtLiveState({ campaignId, accountId, userId, enabled }) {
  const [live, setLive] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [loadError, setLoadError] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const [pollExpired, setPollExpired] = useState(false);
  const snapshotIdRef = useRef(null);
  const autoStartedRef = useRef(false);
  const pollStartedAtRef = useRef(null);
  const mountedRef = useRef(true);

  // Set in the body as well: StrictMode's mount → cleanup → mount would
  // otherwise leave it false and every load would be discarded.
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback(async () => {
    if (!enabled || !campaignId) return null;
    try {
      const res = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}/gt-live-state`), {
        params: { accountId, userId, ...(snapshotIdRef.current ? { have: snapshotIdRef.current } : {}) },
      });
      if (!mountedRef.current) return null;
      const body = res.data || {};
      if (body.data) setData(body.data);
      // An unreadable snapshot object is a read error, not "no snapshot":
      // keep whatever grid is already on screen.
      if (!body.snapshot && !body.snapshotUnreadable) setData(null);
      if (!body.snapshotUnreadable) snapshotIdRef.current = body.snapshot?.snapshotId || null;
      setLive(body);
      setLoadError(null);
      return body;
    } catch (err) {
      if (mountedRef.current) setLoadError(messageOf(err, 'Could not load the GT campaign state'));
      return null;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [enabled, campaignId, accountId, userId]);

  const startRefresh = useCallback(async (trigger) => {
    setRequesting(true);
    setRequestError(null);
    setPollExpired(false);
    try {
      await api.post(getApiUrl(`/api/v2/campaigns/${campaignId}/gt-live-state/refresh`), {
        accountId, userId, trigger,
      });
      pollStartedAtRef.current = Date.now();
      await load();
    } catch (err) {
      if (mountedRef.current) setRequestError(messageOf(err, 'Could not start the GT refresh'));
    } finally {
      if (mountedRef.current) setRequesting(false);
    }
  }, [campaignId, accountId, userId, load]);

  // First load.
  useEffect(() => {
    if (!enabled) return;
    setLoading(true);
    load();
  }, [enabled, load]);

  // Cold cache: one automatic read-only fetch per visit — unless the last
  // attempt hit an access or configuration error that retrying cannot fix.
  useEffect(() => {
    if (!live || autoStartedRef.current) return;
    if (live.identity?.state !== 'launched') return;
    if (live.snapshot || live.inflight || live.accessError || live.configError) return;
    autoStartedRef.current = true;
    startRefresh('initial');
  }, [live, startRefresh]);

  // Poll while a refresh is in flight, within a budget.
  useEffect(() => {
    if (!live?.inflight) return undefined;
    if (pollStartedAtRef.current == null) pollStartedAtRef.current = Date.now();
    if (Date.now() - pollStartedAtRef.current >= POLL_BUDGET_MS) {
      setPollExpired(true);
      return undefined;
    }
    const timer = setTimeout(load, POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [live, load]);

  useEffect(() => {
    if (live && !live.inflight) pollStartedAtRef.current = null;
  }, [live]);

  const refresh = useCallback(() => startRefresh('manual'), [startRefresh]);

  return {
    live, data, loading, loadError, requestError, pollExpired,
    refreshing: requesting || Boolean(live?.inflight),
    refresh,
    reload: load,
  };
}
